import { useState, useCallback, useRef, useEffect } from "react";
import { MagnifyingGlass, Download, ArrowsLeftRight, Image } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";

function PlayerSearch({ onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const db = useRef(null);
  const search = useCallback(async (v) => {
    if (v.length < 2) { setResults([]); return; }
    try { const r = await axios.get(`${API}/players/search?q=${encodeURIComponent(v)}&numQbs=2&numTeams=12&ppr=1`); setResults((r.data||[]).slice(0,6)); setShow(true); } catch {}
  }, []);
  const onChange = (e) => { setQ(e.target.value); clearTimeout(db.current); db.current = setTimeout(() => search(e.target.value), 200); };
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#555]" />
        <input type="text" value={q} onChange={onChange} onFocus={() => results.length > 0 && setShow(true)} placeholder="Search player..." className="w-full h-9 pl-8 pr-3 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none" autoComplete="off" />
      </div>
      {show && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#171717] border border-[#262626] rounded-sm z-50">
          {results.map((p, i) => (
            <button key={i} onClick={() => { onSelect(p); setQ(""); setShow(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#262626] text-xs border-b border-[#1a1a1a] last:border-0">
              <span className="text-white">{p.name}</span><span className="text-[#555] ml-auto">{p.position} · {p.team} · {p.value?.toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CardGenerator({ user }) {
  const [cardType, setCardType] = useState("stat");
  const [rendering, setRendering] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  // Stat card fields
  const [eyebrow, setEyebrow] = useState("DYNASTY TRADE DATA");
  const [bigStat, setBigStat] = useState("40%");
  const [contextLine, setContextLine] = useState('is the <b>average value gap</b> in a dynasty trade');
  const [subline, setSubline] = useState('Based on 125,418 real dynasty trades. "Fair" never meant "even."');
  const [source, setSource] = useState("Source: 125,418 gradeable trades");

  // Spotlight card fields
  const [player, setPlayer] = useState(null);
  const [verdict, setVerdict] = useState("BUY");
  const [take, setTake] = useState("");
  const [takeLoading, setTakeLoading] = useState(false);

  // Trade card fields
  const [tradeData, setTradeData] = useState(null);
  const [tradeLoading, setTradeLoading] = useState(false);

  const pullTrade = async (minGap = 85) => {
    setTradeLoading(true);
    try {
      const res = await axios.get(`${API}/admin/card-data/trade?min_gap=${minGap}`, { withCredentials: true, headers: user?.session_token ? { Authorization: `Bearer ${user.session_token}` } : {} });
      if (res.data && !res.data.error) setTradeData(res.data);
      else alert("No matching trade found — try a lower threshold");
    } catch (e) { console.error(e); }
    setTradeLoading(false);
  };

  const generateTake = async () => {
    if (!player) return;
    setTakeLoading(true);
    try {
      const res = await axios.post(`${API}/admin/generate-take`, {
        name: player.name, position: player.position, team: player.team,
        value: player.value, trend: player.trend || 0, verdict,
      });
      if (res.data.take) setTake(res.data.take);
    } catch (e) { console.error(e); }
    setTakeLoading(false);
  };

  const buildData = () => {
    if (cardType === "stat") {
      return { EYEBROW: eyebrow, BIG_STAT: bigStat, CONTEXT_LINE: contextLine, SUBLINE: subline, SOURCE: source };
    }
    if (cardType === "spot" && player) {
      const trend = player.trend || player.trend30Day || 0;
      return {
        PLAYER_PHOTO_URL: player.sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${player.sleeperId}.jpg` : "",
        PLAYER_NAME: player.name,
        PLAYER_META: `${player.position} — ${player.team}  •  ${player.age || "?"} yrs`,
        VERDICT: verdict,
        SF_VALUE: (player.value || 0).toLocaleString(),
        TREND: trend > 0 ? `+${trend}` : `${trend}`,
        POS_RANK: `${player.position}${player.posRank || "?"}`,
        TAKE: take || "Watch this player closely in dynasty leagues.",
      };
    }
    if (cardType === "trade" && tradeData) {
      const a = tradeData.side_a || [];
      const b = tradeData.side_b || [];
      return {
        TIER_BADGE: tradeData.tier || "LOPSIDED",
        A_ASSET_1: a[0]?.name || "", A_ASSET_1_SUB: a[0] ? `${a[0].position} - ${a[0].team}` : "",
        A_ASSET_2: a[1]?.name || "", A_ASSET_2_SUB: a[1] ? `${a[1].position} - ${a[1].team}` : "",
        B_ASSET_1: b[0]?.name || "", B_ASSET_1_SUB: b[0] ? `${b[0].position} - ${b[0].team}` : "",
        B_ASSET_2: b[1]?.name || "", B_ASSET_2_SUB: b[1] ? `${b[1].position} - ${b[1].team}` : "",
        GAP_PCT: `${tradeData.gap_pct}%`,
        GAP_CONTEXT: `More lopsided than ${tradeData.percentile}% of all dynasty trades — ${tradeData.format}`,
      };
    }
    return {};
  };

  const renderCard = async () => {
    setRendering(true); setImageUrl(null);
    try {
      const res = await axios.post(`${API}/admin/render-card`, { type: cardType, data: buildData() });
      if (res.data.image_base64) {
        setImageUrl(`data:image/png;base64,${res.data.image_base64}`);
      }
    } catch (e) { console.error(e); alert("Render failed: " + (e.response?.data?.detail || e.message)); }
    setRendering(false);
  };

  const downloadImage = () => {
    if (!imageUrl) return;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `dtm-${cardType}-card-${Date.now()}.png`;
    a.click();
  };

  if (!user?.is_admin) return <div className="p-8 text-center text-[#888]">Admin access required</div>;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h2 className="font-heading text-2xl text-white uppercase mb-1">CARD GENERATOR</h2>
      <p className="text-xs text-[#888] mb-6">Generate branded social media cards from live app data.</p>

      {/* Card Type Selector */}
      <div className="flex gap-2 mb-6">
        {[{ val: "stat", label: "Stat Card" }, { val: "trade", label: "Trade Card" }, { val: "spot", label: "Spotlight Card" }].map(t => (
          <button key={t.val} onClick={() => { setCardType(t.val); setImageUrl(null); }} className={`px-4 py-2 text-xs font-bold uppercase rounded-sm transition-colors ${cardType === t.val ? "bg-[#ccff00] text-black" : "border border-[#262626] text-[#888] hover:border-[#444]"}`}>{t.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Panel */}
        <div className="border border-[#262626] bg-[#171717] p-5 space-y-4">
          <h3 className="text-xs font-bold text-[#ccff00] uppercase mb-2">DATA INPUT</h3>

          {cardType === "stat" && (
            <>
              <div><label className="text-[10px] text-[#555] uppercase block mb-1">Eyebrow</label><input value={eyebrow} onChange={e => setEyebrow(e.target.value)} className="w-full h-8 px-3 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm" /></div>
              <div><label className="text-[10px] text-[#555] uppercase block mb-1">Big Stat</label><input value={bigStat} onChange={e => setBigStat(e.target.value)} className="w-full h-8 px-3 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm" /></div>
              <div><label className="text-[10px] text-[#555] uppercase block mb-1">Context Line (HTML ok)</label><textarea value={contextLine} onChange={e => setContextLine(e.target.value)} className="w-full h-16 px-3 py-2 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm" /></div>
              <div><label className="text-[10px] text-[#555] uppercase block mb-1">Subline</label><input value={subline} onChange={e => setSubline(e.target.value)} className="w-full h-8 px-3 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm" /></div>
              <div><label className="text-[10px] text-[#555] uppercase block mb-1">Source</label><input value={source} onChange={e => setSource(e.target.value)} className="w-full h-8 px-3 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm" /></div>
            </>
          )}

          {cardType === "spot" && (
            <>
              <div><label className="text-[10px] text-[#555] uppercase block mb-1">Player</label><PlayerSearch onSelect={p => { setPlayer(p); setTake(""); }} /></div>
              {player && (
                <div className="flex items-center gap-3 px-3 py-2 bg-[#0a0a0a] border border-[#ccff00]/30 rounded-sm">
                  {player.sleeperId && <img src={`https://sleepercdn.com/content/nfl/players/thumb/${player.sleeperId}.jpg`} alt="" className="w-10 h-10 rounded-sm object-cover" onError={e => e.target.style.display='none'} />}
                  <div>
                    <p className="text-sm text-white font-bold">{player.name}</p>
                    <p className="text-[10px] text-[#888]">{player.position} · {player.team} · Value: {player.value?.toLocaleString()} · Trend: {(player.trend||0) > 0 ? "+" : ""}{player.trend||0} · {player.position}#{player.posRank || "?"}</p>
                  </div>
                </div>
              )}
              <div><label className="text-[10px] text-[#555] uppercase block mb-1">Verdict</label>
                <div className="flex gap-2">{["BUY", "HOLD", "SELL"].map(v => <button key={v} onClick={() => setVerdict(v)} className={`px-3 py-1.5 text-xs font-bold rounded-sm ${verdict === v ? "bg-[#ccff00] text-black" : "border border-[#262626] text-[#888]"}`}>{v}</button>)}</div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] text-[#555] uppercase">Take / Analysis</label>
                  <button onClick={generateTake} disabled={!player || takeLoading} className="text-[9px] text-[#ccff00] hover:underline disabled:opacity-50">{takeLoading ? "Generating..." : "AI Generate →"}</button>
                </div>
                <textarea value={take} onChange={e => setTake(e.target.value)} placeholder="The dip is about target competition, not talent..." className="w-full h-20 px-3 py-2 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm placeholder:text-[#555]" />
              </div>
            </>
          )}

          {cardType === "trade" && (
            <>
              <div className="flex gap-2">
                <button onClick={() => pullTrade(85)} disabled={tradeLoading} className="px-3 py-2 text-xs font-bold border border-[#ccff00]/30 text-[#ccff00] rounded-sm hover:bg-[#ccff00]/10 disabled:opacity-50">Pull Top 5% Trade</button>
                <button onClick={() => pullTrade(96)} disabled={tradeLoading} className="px-3 py-2 text-xs font-bold border border-[#ff3b30]/30 text-[#ff3b30] rounded-sm hover:bg-[#ff3b30]/10 disabled:opacity-50">Pull Top 1% Trade</button>
              </div>
              {tradeData && !tradeData.error && (
                <div className="space-y-2">
                  <div className="px-3 py-2 bg-[#0a0a0a] border border-[#262626] rounded-sm">
                    <p className="text-[9px] text-[#ff6b6b] uppercase font-bold mb-1">Side A</p>
                    {tradeData.side_a?.map((p, i) => <p key={i} className="text-xs text-white">{p.name} ({p.position}) — {p.value?.toLocaleString()}</p>)}
                  </div>
                  <div className="px-3 py-2 bg-[#0a0a0a] border border-[#262626] rounded-sm">
                    <p className="text-[9px] text-[#4ade80] uppercase font-bold mb-1">Side B</p>
                    {tradeData.side_b?.map((p, i) => <p key={i} className="text-xs text-white">{p.name} ({p.position}) — {p.value?.toLocaleString()}</p>)}
                  </div>
                  <p className="text-xs text-[#ccff00]">{tradeData.tier} — {tradeData.gap_pct}% gap (top {100 - tradeData.percentile}%)</p>
                </div>
              )}
            </>
          )}

          <button onClick={renderCard} disabled={rendering} className="w-full h-10 btn-primary rounded-sm text-sm font-bold uppercase disabled:opacity-50 flex items-center justify-center gap-2">
            {rendering ? "RENDERING..." : <><Image size={16} /> RENDER TO PNG</>}
          </button>
        </div>

        {/* Preview / Output */}
        <div className="border border-[#262626] bg-[#171717] p-5">
          <h3 className="text-xs font-bold text-[#ccff00] uppercase mb-3">OUTPUT</h3>
          {imageUrl ? (
            <div>
              <img src={imageUrl} alt="Generated card" className="w-full rounded-sm border border-[#262626] mb-3" />
              <button onClick={downloadImage} className="w-full h-9 flex items-center justify-center gap-2 border border-[#ccff00] text-[#ccff00] font-bold text-xs rounded-sm hover:bg-[#ccff00]/10">
                <Download size={14} /> DOWNLOAD PNG
              </button>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-[#555] text-xs">
              {rendering ? "Rendering card..." : "Fill in the data and click Render to generate a card"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
